import { Controller } from '@hotwired/stimulus';

// The add-on form's interactive layer. Everything here is an UPGRADE of a
// control that already works: the toggles are checkboxes, the stepper is a
// number input, the status control is radio buttons, and the Save button at the
// bottom posts the form. With JavaScript off you lose the live preview and the
// unsaved bar, not the ability to edit an add-on.
export default class extends Controller {
  static targets = [
    'saveBar', 'name', 'detailLine', 'priceWith', 'priceWithout', 'samePrice', 'imageCount',
    'toggleInput', 'toggleButton', 'placement', 'placementNote', 'placementCode', 'statusRow',
    'rawCode', 'rawCodeToggle', 'internalCode', 'previewName', 'previewDetail', 'previewPrice', 'previewImage',
  ];

  static values = { notes: Object };

  connect() {
    this.dirty = false;
    this.beforeUnload = (event) => {
      if (!this.dirty) return undefined;

      event.preventDefault();
      // Chrome shows its own wording and only needs returnValue set.
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', this.beforeUnload);

    this.upgradeToggles();
    this.toggleSamePrice();
    this.placementChanged();
    this.statusChanged();
    this.renderPreview();
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.beforeUnload);
  }

  // ---- unsaved changes -------------------------------------------------------
  touch() {
    this.dirty = true;
    this.saveBarTarget.hidden = false;
    // Re-mirrored here as well as on the price field's own action: whichever way
    // the package price was changed, a ticked "same price" has to stay true.
    this.mirrorPrice();
    this.renderPreview();
  }

  discard() {
    this.dirty = false;
    window.location.reload();
  }

  // ---- toggles ---------------------------------------------------------------
  // The checkbox stays in the DOM and stays the thing that submits; the switch
  // is a button in front of it. Swapped here rather than in the template so a
  // JS failure leaves a usable checkbox rather than nothing at all.
  upgradeToggles() {
    this.toggleInputTargets.forEach((input, index) => {
      const button = this.toggleButtonTargets[index];
      if (!button) return;

      input.hidden = true;
      button.hidden = false;
      button.setAttribute('aria-pressed', input.checked ? 'true' : 'false');
    });
  }

  toggle(event) {
    const button = event.currentTarget;
    const index = this.toggleButtonTargets.indexOf(button);
    const input = this.toggleInputTargets[index];
    if (!input) return;

    input.checked = !input.checked;
    button.setAttribute('aria-pressed', input.checked ? 'true' : 'false');
    this.touch();
  }

  // ---- pricing ----------------------------------------------------------------
  toggleSamePrice() {
    const same = this.samePriceTarget.checked;

    this.priceWithoutTarget.disabled = false; // a disabled field would not submit
    this.priceWithoutTarget.readOnly = same;
    this.priceWithoutTarget.classList.toggle('is-mirrored', same);

    if (same) this.mirrorPrice();
    this.renderPreview();
  }

  // Mirrors into the field rather than skipping it: both prices are always
  // submitted, because there is no "same price" column to submit instead.
  mirrorPrice() {
    if (!this.samePriceTarget.checked) return;

    this.priceWithoutTarget.value = this.priceWithTarget.value;
    this.renderPreview();
  }

  // ---- stepper -----------------------------------------------------------------
  stepUp() {
    this.imageCountTarget.value = Math.max(1, Number(this.imageCountTarget.value || 1) + 1);
    this.touch();
  }

  stepDown() {
    this.imageCountTarget.value = Math.max(1, Number(this.imageCountTarget.value || 1) - 1);
    this.touch();
  }

  // ---- placement ------------------------------------------------------------------
  placementChanged() {
    const value = this.placementTarget.value;
    const notes = this.notesValue || {};

    this.placementNoteTarget.textContent = notes[value] || notes[''] || '';
    this.placementCodeTarget.textContent = value || 'none';

    if (this.hasInternalCodeTarget && !this.rawCodeToggleTarget.checked) {
      this.internalCodeTarget.textContent = value || '—';
    }
  }

  // Exactly one css_name field is ever enabled, so the value that saves is never
  // in doubt: ticking this disables the dropdown (a disabled control is not
  // submitted) and hands over to the text box.
  toggleRawCode() {
    const raw = this.rawCodeToggleTarget.checked;

    this.rawCodeTarget.disabled = !raw;
    this.placementTarget.disabled = raw;

    if (raw && !this.rawCodeTarget.value) this.rawCodeTarget.value = this.placementTarget.value;

    this.touch();
  }

  // ---- status ------------------------------------------------------------------------
  // Each option carries its own description in the markup, so there is nothing to
  // rewrite here -- only the selected row to mark. `:has(input:checked)` does the
  // same in CSS; this keeps it working in a browser without `:has`.
  statusChanged() {
    this.statusRowTargets.forEach((row) => {
      const input = row.querySelector('input[type="radio"]');
      row.classList.toggle('is-selected', Boolean(input && input.checked));
    });
  }

  // ---- live store card ------------------------------------------------------------------
  imageChanged(event) {
    this.previewImageTarget.src = (event.detail && event.detail.url) || '';
    this.touch();
  }

  renderPreview() {
    if (!this.hasPreviewNameTarget) return;

    this.previewNameTarget.textContent = this.nameTarget.value || 'Untitled add-on';
    this.previewDetailTarget.textContent = this.detailLineTarget.value || '';

    const price = Number(this.priceWithoutTarget.value || 0);
    this.previewPriceTarget.textContent = Number.isFinite(price)
      ? price.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
      : '';
  }
}
